import React from 'react';
import styled from 'styled-components';

import { MdPictureAsPdf } from 'react-icons/md';
import sharedStyles from '../styles';
import breakpoints from '../breakpoints';
import ExternalLinkIcon from '../svgs/external-link.svg';

import { useUiComponentsInternationalContext } from '@repo/static-translations';
interface DownloadLink {
  link?: {
    file: {
      url: string;
      details: {
        size: number;
      };
    };
  };
  text: string;
}

interface SupportManual {
  asset: {
    assetId: string;
    url: string;
    size: number;
  };
  label?: string;
}

interface DownloadsSection {
  background: string;
  textColor: string;
  heading: string;
  supportLink?: string;
}

interface ProductDownloadsProps {
  downloads?: DownloadLink[];
  productManualLink?: string;
  supportManuals?: SupportManual[];
  downloadsSection: DownloadsSection;
}

const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

const DownloadsContainer = styled.div<{
  background: string;
  textColor: string;
}>`
  padding: 1.5rem 1.5rem;
  background-color: ${({ background }) => background};
  margin-top: ${sharedStyles.spacing['spacing-md']};

  @media only screen and (min-width: ${breakpoints.md}) {
    margin: 1.25rem 0;
    padding: 1.5rem 1.5rem;
  }

  h2 {
    font-weight: 600;
    font-size: 1.25rem;
    color: ${({ textColor }) => textColor};
    margin: 0 0 ${sharedStyles.spacing['spacing-md']};
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: flex;
    height: 100%;
    align-items: center;
    color: ${({ textColor }) => textColor};
    font-weight: 700;
    margin-top: 1rem;
    justify-content: space-between;
    text-align: left;
    width: 100%;

    a {
      color: ${({ textColor }) => textColor};
      font-weight: 400;
      text-decoration: underline;
      display: flex;
      align-items: center;

      &:hover {
        text-decoration: none;
      }

      span {
        margin-right: 10px;
      }

      svg {
        margin-right: 10px;
        flex-shrink: 0;
        max-width: ${sharedStyles.spacing['spacing-md']};
        max-height: ${sharedStyles.spacing['spacing-md']};
      }
    }
  }
`;

const IconPDF = styled(MdPictureAsPdf)`
  width: 30px;
  height: 30px;
`;

const ProductDownloads: React.FC<ProductDownloadsProps> = ({
  downloads = [],
  productManualLink = '',
  supportManuals = [],
  downloadsSection,
}) => {
  const { tt } = useUiComponentsInternationalContext();

  return (
    <DownloadsContainer
      background={downloadsSection.background}
      textColor={downloadsSection.textColor}
    >
      <h2>{downloadsSection.heading}</h2>
      <ul>
        {productManualLink && (
          <li key={productManualLink}>
            <a href={productManualLink} target="_blank" rel="noreferrer">
              <IconPDF />
              {tt(s => s.productCommon.productManualLink)}
            </a>
          </li>
        )}

        {supportManuals &&
          supportManuals.map(({ asset, label }) => (
            <li key={asset?.assetId}>
              <a href={asset?.url} target="_blank" rel="noreferrer">
                <IconPDF />
                {label || tt(s => s.productCommon.productManualLink)} (
                {formatBytes(asset?.size, 2)})
              </a>
            </li>
          ))}

        {downloads &&
          downloads.map(download => {
            if (download.link) {
              return (
                <li key={download.link.file.url}>
                  <a
                    href={download.link.file.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <IconPDF />
                    {download.text} (
                    {formatBytes(download.link.file.details.size, 2)})
                  </a>
                </li>
              );
            }

            return null;
          })}

        {downloadsSection?.supportLink &&
          downloadsSection.supportLink.split('.').pop() !== 'pdf' && (
            <li key={downloadsSection.supportLink}>
              <a
                href={downloadsSection.supportLink}
                target="_blank"
                rel="noreferrer"
              >
                <span>{tt(s => s.productCommon.visitSupportSiteLink)}</span>
                <ExternalLinkIcon
                  fill={downloadsSection.textColor}
                  stroke={downloadsSection.textColor}
                />
              </a>
            </li>
          )}
      </ul>
    </DownloadsContainer>
  );
};

export default ProductDownloads;
